import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import {
  Contract,
  ContractListResponse,
  ContractListItem,
  EmployeeOption,
  DepartmentOption,
  GradeOption,
  ProjectOption,
  CurrencyOption,
  SalaryTypeOption,
  SectionOption,
  AccountOption,
  ApiResponse,
} from '@/api/types'

/**
 * Contract management service
 */
export class ContractService {
  /**
   * Get paginated contracts for a branch and employee (path: branchId/empId/pageNumber/pageSize).
   */
  async getAllContracts(
    branchId: number,
    empId: number,
    pageNumber: number,
    pageSize: number
  ): Promise<ContractListResponse> {
    const url = endpoints.contracts.getAll(branchId, empId, pageNumber, pageSize)
    const response = await apiClient.get<ApiResponse<ContractListResponse> | ContractListResponse | any>(url)
    const payload = response.data

    if (payload && typeof payload === 'object') {
      if ('success' in payload && 'data' in payload) {
        return payload.data as ContractListResponse
      }

      if ('data' in payload && Array.isArray(payload.data)) {
        return {
          totalRecords: payload.totalRecords ?? payload.data.length,
          pageNumber: payload.pageNumber ?? pageNumber,
          pageSize: payload.pageSize ?? pageSize,
          data: payload.data as ContractListItem[],
        }
      }
    }

    if (Array.isArray(payload)) {
      return {
        totalRecords: payload.length,
        pageNumber,
        pageSize,
        data: payload as ContractListItem[],
      }
    }

    return {
      totalRecords: payload?.totalRecords ?? 0,
      pageNumber: payload?.pageNumber ?? pageNumber,
      pageSize: payload?.pageSize ?? pageSize,
      data: payload?.data ?? [],
    }
  }

  /**
   * Get contract by ID
   */
  async getContractById(id: number, branchId: number): Promise<Contract> {
    const url = endpoints.contracts.getById(id, branchId)
    const response = await apiClient.get<ApiResponse<Contract> | Contract | any>(url)
    const payload = response.data

    if (payload && typeof payload === 'object') {
      if ('success' in payload && 'data' in payload) {
        return payload.data as Contract
      }
      if ('data' in payload) {
        return payload.data as Contract
      }
    }

    return payload as Contract
  }

  /**
   * Get all employees for dropdown
   */
  async getAllEmployees(branchId: number): Promise<EmployeeOption[]> {
    try {
      if (!branchId || branchId === 0) {
        console.error('Invalid branchId provided:', branchId)
        return []
      }
      
      // Try Contract endpoint first, if it fails with 404, use Employee endpoint
      const contractUrl = endpoints.contracts.getAllEmployees(branchId)
      console.log('🔵 [ContractService.getAllEmployees] Trying Contract endpoint:', contractUrl, 'branchId:', branchId)
      
      try {
        const response = await apiClient.get<ApiResponse<EmployeeOption[]> | EmployeeOption[] | any>(contractUrl)
        console.log('🔵 [ContractService.getAllEmployees] Contract endpoint succeeded')
        console.log('🔵 [ContractService.getAllEmployees] Full response:', response)
        console.log('🔵 [ContractService.getAllEmployees] Response data:', response.data)
        console.log('🔵 [ContractService.getAllEmployees] Response data type:', typeof response.data)
        console.log('🔵 [ContractService.getAllEmployees] Is array:', Array.isArray(response.data))
        
        const payload = response.data

        // Handle different response structures
        // Check for direct array first (most common case)
        if (Array.isArray(payload)) {
          console.log('🔵 [ContractService.getAllEmployees] Employees from direct array:', payload.length)
          if (payload.length === 0) {
            console.warn('⚠️ [ContractService.getAllEmployees] API returned empty array')
            return []
          }
          console.log('🔵 [ContractService.getAllEmployees] First employee sample:', payload[0])
          const mapped = (payload as any[]).map((emp: any) => {
            const empId = emp.id || emp.empId || emp.employeeId || 0
            const firstName = emp.empFirstNameEn || emp.firstNameEn || emp.firstName || ''
            const secondName = emp.empSecondNameEn || emp.secondNameEn || ''
            const lastName = emp.empLastNameEn || emp.lastNameEn || emp.lastName || ''
            // Construct full name: FirstName SecondName LastName
            const fullName = [firstName, secondName, lastName].filter(Boolean).join(' ').trim() || `Employee ${empId}`
            return {
              empId: empId,
              empCode: emp.empCode || emp.employeeCode || '',
              empNationalityId: emp.empNationalityId ?? emp.EmpNationalityId,
              empFirstNameEn: firstName,
              empSecondNameEn: secondName,
              empLastNameEn: lastName,
              empFirstNameAr: emp.empFirstNameAr || emp.firstNameAr || '',
              empLastNameAr: emp.empLastNameAr || emp.lastNameAr || '',
              fullName: fullName,
            }
          }).filter((emp: EmployeeOption) => emp.empId > 0)
          console.log('✅ [ContractService.getAllEmployees] Successfully mapped employees:', mapped.length)
          if (mapped.length === 0) {
            console.warn('⚠️ [ContractService.getAllEmployees] All employees were filtered out (invalid IDs)')
          }
          return mapped
        }

        // Handle object responses
        if (payload && typeof payload === 'object' && !Array.isArray(payload)) {
          // Case 1: { success: true, data: [...] }
          if ('success' in payload && 'data' in payload && Array.isArray(payload.data)) {
            const data = payload.data as EmployeeOption[]
            console.log('🔵 [ContractService.getAllEmployees] Employees from success.data:', data.length)
            const mapped = data.map((emp: any) => {
              const empId = emp.id || emp.empId || emp.employeeId || 0
              const firstName = emp.empFirstNameEn || emp.firstNameEn || emp.firstName || ''
              const secondName = emp.empSecondNameEn || emp.secondNameEn || ''
              const lastName = emp.empLastNameEn || emp.lastNameEn || emp.lastName || ''
              // Construct full name: FirstName SecondName LastName
              const fullName = [firstName, secondName, lastName].filter(Boolean).join(' ').trim() || `Employee ${empId}`
              return {
                empId: empId,
                empCode: emp.empCode || emp.employeeCode || '',
                empNationalityId: emp.empNationalityId ?? emp.EmpNationalityId,
                empFirstNameEn: firstName,
                empSecondNameEn: secondName,
                empLastNameEn: lastName,
                empFirstNameAr: emp.empFirstNameAr || emp.firstNameAr || '',
                empLastNameAr: emp.empLastNameAr || emp.lastNameAr || '',
                fullName: fullName,
              }
            }).filter((emp: EmployeeOption) => emp.empId > 0)
            console.log('✅ [ContractService.getAllEmployees] Successfully mapped employees:', mapped.length)
            return mapped
          }
          // Case 2: { data: [...] }
          if ('data' in payload && Array.isArray(payload.data)) {
            const data = payload.data as any[]
            console.log('🔵 [ContractService.getAllEmployees] Employees from data:', data.length)
            const mapped = data.map((emp: any) => {
              const empId = emp.id || emp.empId || emp.employeeId || 0
              const firstName = emp.empFirstNameEn || emp.firstNameEn || emp.firstName || ''
              const secondName = emp.empSecondNameEn || emp.secondNameEn || ''
              const lastName = emp.empLastNameEn || emp.lastNameEn || emp.lastName || ''
              // Construct full name: FirstName SecondName LastName
              const fullName = [firstName, secondName, lastName].filter(Boolean).join(' ').trim() || `Employee ${empId}`
              return {
                empId: empId,
                empCode: emp.empCode || emp.employeeCode || '',
                empNationalityId: emp.empNationalityId ?? emp.EmpNationalityId,
                empFirstNameEn: firstName,
                empSecondNameEn: secondName,
                empLastNameEn: lastName,
                empFirstNameAr: emp.empFirstNameAr || emp.firstNameAr || '',
                empLastNameAr: emp.empLastNameAr || emp.lastNameAr || '',
                fullName: fullName,
              }
            }).filter((emp: EmployeeOption) => emp.empId > 0)
            console.log('✅ [ContractService.getAllEmployees] Successfully mapped employees:', mapped.length)
            return mapped
          }
        }

        console.warn('⚠️ [ContractService.getAllEmployees] No employees found in response.')
        console.warn('Payload type:', typeof payload)
        console.warn('Is Array:', Array.isArray(payload))
        console.warn('Payload keys:', payload && typeof payload === 'object' ? Object.keys(payload) : 'N/A')
        console.warn('Full payload:', JSON.stringify(payload, null, 2))
        return []
      } catch (contractError: any) {
        // If Contract endpoint fails with 404, try Employee endpoint
        if (contractError.response?.status === 404) {
          console.warn('⚠️ [ContractService.getAllEmployees] Contract endpoint returned 404, trying Employee endpoint')
          const { employeesService } = await import('@/services/employeesService')
          
          // Fetch all employees using Employee service (with pagination, get first 1000)
          const employeeResponse = await employeesService.getAllEmployees(branchId, 1, 1000)
          const employeesList = employeeResponse.data || []
          
          console.log('🔵 [ContractService.getAllEmployees] Employee endpoint returned:', employeesList.length, 'employees')
          
          if (employeesList.length === 0) {
            console.warn('⚠️ [ContractService.getAllEmployees] Employee endpoint also returned empty array')
            return []
          }
          
          // Map Employee format to EmployeeOption format
          const mapped = employeesList.map((emp: any) => {
            const empId = emp.id || emp.empId || emp.employeeId || 0
            const firstName = emp.empFirstNameEn || emp.firstNameEn || emp.firstName || ''
            const secondName = emp.empSecondNameEn || emp.secondNameEn || ''
            const lastName = emp.empLastNameEn || emp.lastNameEn || emp.lastName || ''
            // Construct full name: FirstName SecondName LastName
            const fullName = [firstName, secondName, lastName].filter(Boolean).join(' ').trim() || `Employee ${empId}`
return {
            empId: empId,
            empCode: emp.empCode || emp.employeeCode || '',
            empNationalityId: emp.empNationalityId ?? emp.EmpNationalityId,
            empFirstNameEn: firstName,
            empSecondNameEn: secondName,
            empLastNameEn: lastName,
            empFirstNameAr: emp.empFirstNameAr || emp.firstNameAr || '',
            empLastNameAr: emp.empLastNameAr || emp.lastNameAr || '',
            fullName: fullName,
          }
        }).filter((emp: EmployeeOption) => emp.empId > 0)
        
          console.log('✅ [ContractService.getAllEmployees] Successfully mapped employees from Employee endpoint:', mapped.length)
          return mapped
        }
        throw contractError
      }
    } catch (error: any) {
      console.error('❌ [ContractService.getAllEmployees] Error fetching employees:', error)
      console.error('Error message:', error.message)
      console.error('Error response:', error.response?.data)
      console.error('Error status:', error.response?.status)
      console.error('Error URL:', error.config?.url)
      return []
    }
  }

  /**
   * Get departments
   */
  async getDepartments(branchId: number): Promise<DepartmentOption[]> {
    try {
      const url = endpoints.contracts.getDepartments(branchId)
      console.log('Fetching departments from:', url)
      const response = await apiClient.get<ApiResponse<DepartmentOption[]> | DepartmentOption[] | any>(url)
      console.log('Departments API response:', response.data)
      const payload = response.data

      // Handle different response structures
      if (payload && typeof payload === 'object') {
        // Case 1: { success: true, data: [...] }
        if ('success' in payload && 'data' in payload && Array.isArray(payload.data)) {
          const data = payload.data as any[]
          console.log('Departments from success.data:', data.length)
          return data.map((dept: any) => ({
            id: dept.id || dept.departmentId || dept.departmetId || 0,
            departmentId: dept.departmentId || dept.id || dept.departmetId || 0,
            departmentNameEn: dept.departmentNameEn || dept.departmentName || dept.nameEn || dept.name,
            departmentNameAr: dept.departmentNameAr || dept.nameAr,
            departmentName: dept.departmentName || dept.departmentNameEn || dept.nameEn || dept.name || dept.departmentNameAr || dept.nameAr,
          })).filter((dept: DepartmentOption) => (dept.id || dept.departmentId || 0) > 0)
        }
        // Case 2: { data: [...] }
        if ('data' in payload && Array.isArray(payload.data)) {
          const data = payload.data as any[]
          console.log('Departments from data:', data.length)
          return data.map((dept: any) => ({
            id: dept.id || dept.departmentId || dept.departmetId || 0,
            departmentId: dept.departmentId || dept.id || dept.departmetId || 0,
            departmentNameEn: dept.departmentNameEn || dept.departmentName || dept.nameEn || dept.name,
            departmentNameAr: dept.departmentNameAr || dept.nameAr,
            departmentName: dept.departmentName || dept.departmentNameEn || dept.nameEn || dept.name || dept.departmentNameAr || dept.nameAr,
          })).filter((dept: DepartmentOption) => (dept.id || dept.departmentId || 0) > 0)
        }
        // Case 3: Direct array in object
        if (Array.isArray(payload)) {
          console.log('Departments from direct array:', payload.length)
          return (payload as any[]).map((dept: any) => ({
            id: dept.id || dept.departmentId || dept.departmetId || 0,
            departmentId: dept.departmentId || dept.id || dept.departmetId || 0,
            departmentNameEn: dept.departmentNameEn || dept.departmentName || dept.nameEn || dept.name,
            departmentNameAr: dept.departmentNameAr || dept.nameAr,
            departmentName: dept.departmentName || dept.departmentNameEn || dept.nameEn || dept.name || dept.departmentNameAr || dept.nameAr,
          })).filter((dept: DepartmentOption) => (dept.id || dept.departmentId || 0) > 0)
        }
      }

      // Case 4: Direct array response
      if (Array.isArray(payload)) {
        console.log('Departments from array payload:', payload.length)
        return (payload as any[]).map((dept: any) => ({
          id: dept.id || dept.departmentId || dept.departmetId || 0,
          departmentId: dept.departmentId || dept.id || dept.departmetId || 0,
          departmentNameEn: dept.departmentNameEn || dept.departmentName || dept.nameEn || dept.name,
          departmentNameAr: dept.departmentNameAr || dept.nameAr,
          departmentName: dept.departmentName || dept.departmentNameEn || dept.nameEn || dept.name || dept.departmentNameAr || dept.nameAr,
        })).filter((dept: DepartmentOption) => (dept.id || dept.departmentId || 0) > 0)
      }

      console.warn('No departments found in response. Payload type:', typeof payload, 'Payload:', payload)
      return []
    } catch (error: any) {
      console.error('Error fetching departments:', error)
      console.error('Error response:', error.response?.data)
      return []
    }
  }

  /**
   * Get grades
   */
  async getGrades(): Promise<GradeOption[]> {
    try {
      const url = endpoints.contracts.getGrade
      console.log('Fetching grades from:', url)
      const response = await apiClient.get<ApiResponse<GradeOption[]> | GradeOption[] | any>(url)
      console.log('Grades API response:', response.data)
      const payload = response.data

      // Handle different response structures
      if (payload && typeof payload === 'object') {
        // Case 1: { success: true, data: [...] }
        if ('success' in payload && 'data' in payload && Array.isArray(payload.data)) {
          const data = payload.data as any[]
          console.log('Grades from success.data:', data.length)
          return data.map((grade: any) => ({
            id: grade.id || grade.gradeId || 0,
            gradeId: grade.gradeId || grade.id || 0,
            lookupNameEn: grade.lookupNameEn,
            lookupNameAr: grade.lookupNameAr,
            gradeNameEn: grade.lookupNameEn || grade.gradeNameEn || grade.gradeName || grade.nameEn || grade.name,
            gradeNameAr: grade.lookupNameAr || grade.gradeNameAr || grade.nameAr,
            gradeName: grade.lookupNameEn || grade.lookupNameAr || grade.gradeNameEn || grade.gradeName || grade.nameEn || grade.name || grade.gradeNameAr || grade.nameAr,
          })).filter((grade: GradeOption) => (grade.id || grade.gradeId || 0) > 0)
        }
        // Case 2: { data: [...] }
        if ('data' in payload && Array.isArray(payload.data)) {
          const data = payload.data as any[]
          console.log('Grades from data:', data.length)
          return data.map((grade: any) => ({
            id: grade.id || grade.gradeId || 0,
            gradeId: grade.gradeId || grade.id || 0,
            lookupNameEn: grade.lookupNameEn,
            lookupNameAr: grade.lookupNameAr,
            gradeNameEn: grade.lookupNameEn || grade.gradeNameEn || grade.gradeName || grade.nameEn || grade.name,
            gradeNameAr: grade.lookupNameAr || grade.gradeNameAr || grade.nameAr,
            gradeName: grade.lookupNameEn || grade.lookupNameAr || grade.gradeNameEn || grade.gradeName || grade.nameEn || grade.name || grade.gradeNameAr || grade.nameAr,
          })).filter((grade: GradeOption) => (grade.id || grade.gradeId || 0) > 0)
        }
        // Case 3: Direct array in object
        if (Array.isArray(payload)) {
          console.log('Grades from direct array:', payload.length)
          return (payload as any[]).map((grade: any) => ({
            id: grade.id || grade.gradeId || 0,
            gradeId: grade.gradeId || grade.id || 0,
            gradeNameEn: grade.lookupNameEn || grade.gradeNameEn || grade.gradeName || grade.nameEn || grade.name,
            gradeNameAr: grade.lookupNameAr || grade.gradeNameAr || grade.nameAr,
            gradeName: grade.lookupNameEn || grade.lookupNameAr || grade.gradeNameEn || grade.gradeName || grade.nameEn || grade.name || grade.gradeNameAr || grade.nameAr,
          })).filter((grade: GradeOption) => (grade.id || grade.gradeId || 0) > 0)
        }
      }

      // Case 4: Direct array response
      if (Array.isArray(payload)) {
        console.log('Grades from array payload:', payload.length)
        return (payload as any[]).map((grade: any) => ({
          id: grade.id || grade.gradeId || 0,
          gradeId: grade.gradeId || grade.id || 0,
          lookupNameEn: grade.lookupNameEn,
          lookupNameAr: grade.lookupNameAr,
          gradeNameEn: grade.lookupNameEn || grade.gradeNameEn || grade.gradeName || grade.nameEn || grade.name,
          gradeNameAr: grade.lookupNameAr || grade.gradeNameAr || grade.nameAr,
          gradeName: grade.lookupNameEn || grade.lookupNameAr || grade.gradeNameEn || grade.gradeName || grade.nameEn || grade.name || grade.gradeNameAr || grade.nameAr,
        })).filter((grade: GradeOption) => (grade.id || grade.gradeId || 0) > 0)
      }

      console.warn('No grades found in response. Payload type:', typeof payload, 'Payload:', payload)
      return []
    } catch (error: any) {
      console.error('Error fetching grades:', error)
      console.error('Error response:', error.response?.data)
      return []
    }
  }

  /**
   * Get projects
   */
  async getProjects(branchId: number): Promise<ProjectOption[]> {
    // Using projects endpoint if available, otherwise return empty
    try {
      const { projectsService } = await import('@/services/projectsService')
      const projects = await projectsService.getAllProjects(branchId, 1, 1000)
      return (projects.data || []).map((p: any) => ({
        id: p.projectId || p.id,
        projectId: p.projectId || p.id,
        projectNameEn: p.projectNameEn,
        projectNameAr: p.projectNameAr,
        projectName: p.projectNameEn || p.projectNameAr || p.projectName,
      }))
    } catch {
      return []
    }
  }

  /**
   * Get currency options
   */
  async getCurrency(): Promise<CurrencyOption[]> {
    try {
      const url = endpoints.contracts.getCurrency
      console.log('Fetching currency from:', url)
      const response = await apiClient.get<ApiResponse<CurrencyOption[]> | CurrencyOption[] | any>(url)
      console.log('Currency API response:', response.data)
      const payload = response.data

      // Handle different response structures
      if (payload && typeof payload === 'object') {
        if ('success' in payload && 'data' in payload && Array.isArray(payload.data)) {
          const data = payload.data as any[]
          console.log('Currency from success.data:', data.length)
          return data.map((curr: any) => ({
            id: curr.id || curr.currencyId || 0,
            currencyId: curr.currencyId || curr.id || 0,
            currencyCode: curr.currencyCode || curr.code,
            currencyName: curr.currencyName || curr.name || curr.currencyCode || curr.code,
          })).filter((curr: CurrencyOption) => (curr.id || curr.currencyId || 0) > 0)
        }
        if ('data' in payload && Array.isArray(payload.data)) {
          const data = payload.data as any[]
          console.log('Currency from data:', data.length)
          return data.map((curr: any) => ({
            id: curr.id || curr.currencyId || 0,
            currencyId: curr.currencyId || curr.id || 0,
            currencyCode: curr.currencyCode || curr.code,
            currencyName: curr.currencyName || curr.name || curr.currencyCode || curr.code,
          })).filter((curr: CurrencyOption) => (curr.id || curr.currencyId || 0) > 0)
        }
        if (Array.isArray(payload)) {
          console.log('Currency from direct array:', payload.length)
          return (payload as any[]).map((curr: any) => ({
            id: curr.id || curr.currencyId || 0,
            currencyId: curr.currencyId || curr.id || 0,
            currencyCode: curr.currencyCode || curr.code,
            currencyName: curr.currencyName || curr.name || curr.currencyCode || curr.code,
          })).filter((curr: CurrencyOption) => (curr.id || curr.currencyId || 0) > 0)
        }
      }

      if (Array.isArray(payload)) {
        console.log('Currency from array payload:', payload.length)
        return (payload as any[]).map((curr: any) => ({
          id: curr.id || curr.currencyId || 0,
          currencyId: curr.currencyId || curr.id || 0,
          currencyCode: curr.currencyCode || curr.code,
          currencyName: curr.currencyName || curr.name || curr.currencyCode || curr.code,
        })).filter((curr: CurrencyOption) => (curr.id || curr.currencyId || 0) > 0)
      }

      console.warn('No currency found in response. Payload type:', typeof payload, 'Payload:', payload)
      return []
    } catch (error: any) {
      console.error('Error fetching currency:', error)
      console.error('Error response:', error.response?.data)
      return []
    }
  }

  /**
   * Get salary type options
   */
  async getSalaryType(): Promise<SalaryTypeOption[]> {
    try {
      const url = endpoints.contracts.getSalaryType
      console.log('Fetching salary type from:', url)
      const response = await apiClient.get<ApiResponse<SalaryTypeOption[]> | SalaryTypeOption[] | any>(url)
      console.log('Salary type API response:', response.data)
      const payload = response.data

      // Handle different response structures
      if (payload && typeof payload === 'object') {
        if ('success' in payload && 'data' in payload && Array.isArray(payload.data)) {
          const data = payload.data as any[]
          console.log('Salary type from success.data:', data.length)
          return data.map((type: any) => ({
            id: type.id || type.salaryTypeId || 0,
            salaryTypeId: type.salaryTypeId || type.id || 0,
            salaryTypeName: type.lookupNameEn || type.lookupName || type.salaryTypeName || type.name || type.typeName || `Type ${type.id || type.salaryTypeId}`,
          })).filter((type: SalaryTypeOption) => (type.id || type.salaryTypeId || 0) > 0)
        }
        if ('data' in payload && Array.isArray(payload.data)) {
          const data = payload.data as any[]
          console.log('Salary type from data:', data.length)
          return data.map((type: any) => ({
            id: type.id || type.salaryTypeId || 0,
            salaryTypeId: type.salaryTypeId || type.id || 0,
            salaryTypeName: type.lookupNameEn || type.lookupName || type.salaryTypeName || type.name || type.typeName || `Type ${type.id || type.salaryTypeId}`,
          })).filter((type: SalaryTypeOption) => (type.id || type.salaryTypeId || 0) > 0)
        }
        if (Array.isArray(payload)) {
          console.log('Salary type from direct array:', payload.length)
          return (payload as any[]).map((type: any) => ({
            id: type.id || type.salaryTypeId || 0,
            salaryTypeId: type.salaryTypeId || type.id || 0,
            salaryTypeName: type.lookupNameEn || type.lookupName || type.salaryTypeName || type.name || type.typeName || `Type ${type.id || type.salaryTypeId}`,
          })).filter((type: SalaryTypeOption) => (type.id || type.salaryTypeId || 0) > 0)
        }
      }

      if (Array.isArray(payload)) {
        console.log('Salary type from array payload:', payload.length)
        return (payload as any[]).map((type: any) => ({
          id: type.id || type.salaryTypeId || 0,
          salaryTypeId: type.salaryTypeId || type.id || 0,
          salaryTypeName: type.lookupNameEn || type.lookupName || type.salaryTypeName || type.name || type.typeName || `Type ${type.id || type.salaryTypeId}`,
        })).filter((type: SalaryTypeOption) => (type.id || type.salaryTypeId || 0) > 0)
      }

      console.warn('No salary type found in response. Payload type:', typeof payload, 'Payload:', payload)
      return []
    } catch (error: any) {
      console.error('Error fetching salary type:', error)
      console.error('Error response:', error.response?.data)
      return []
    }
  }

  /**
   * Get section options
   */
  async getSection(): Promise<SectionOption[]> {
    try {
      const url = endpoints.contracts.getSection
      console.log('Fetching section from:', url)
      const response = await apiClient.get<ApiResponse<SectionOption[]> | SectionOption[] | any>(url)
      console.log('Section API response:', response.data)
      const payload = response.data

      // Handle different response structures
      if (payload && typeof payload === 'object') {
        if ('success' in payload && 'data' in payload && Array.isArray(payload.data)) {
          const data = payload.data as any[]
          console.log('Section from success.data:', data.length)
          return data.map((section: any) => ({
            id: section.id || section.sectionId || 0,
            sectionId: section.sectionId || section.id || 0,
            sectionName: section.sectionName || section.name || `Section ${section.id || section.sectionId}`,
          })).filter((section: SectionOption) => (section.id || section.sectionId || 0) > 0)
        }
        if ('data' in payload && Array.isArray(payload.data)) {
          const data = payload.data as any[]
          console.log('Section from data:', data.length)
          return data.map((section: any) => ({
            id: section.id || section.sectionId || 0,
            sectionId: section.sectionId || section.id || 0,
            sectionName: section.sectionName || section.name || `Section ${section.id || section.sectionId}`,
          })).filter((section: SectionOption) => (section.id || section.sectionId || 0) > 0)
        }
        if (Array.isArray(payload)) {
          console.log('Section from direct array:', payload.length)
          return (payload as any[]).map((section: any) => ({
            id: section.id || section.sectionId || 0,
            sectionId: section.sectionId || section.id || 0,
            sectionName: section.sectionName || section.name || `Section ${section.id || section.sectionId}`,
          })).filter((section: SectionOption) => (section.id || section.sectionId || 0) > 0)
        }
      }

      if (Array.isArray(payload)) {
        console.log('Section from array payload:', payload.length)
        return (payload as any[]).map((section: any) => ({
          id: section.id || section.sectionId || 0,
          sectionId: section.sectionId || section.id || 0,
          sectionName: section.sectionName || section.name || `Section ${section.id || section.sectionId}`,
        })).filter((section: SectionOption) => (section.id || section.sectionId || 0) > 0)
      }

      console.warn('No section found in response. Payload type:', typeof payload, 'Payload:', payload)
      return []
    } catch (error: any) {
      console.error('Error fetching section:', error)
      console.error('Error response:', error.response?.data)
      return []
    }
  }

  /**
   * Get accounts for salary accounts dropdown
   */
  async getAccounts(branchId: number): Promise<AccountOption[]> {
    try {
      console.log('Fetching accounts for branchId:', branchId)
      // Use paymentsService.getAllAccounts which is the standard way to get accounts
      const { paymentsService } = await import('@/services/paymentsService')
      const accounts = await paymentsService.getAllAccounts(branchId)
      console.log('Accounts API response:', accounts?.length || 0, 'accounts')
      console.log('Accounts API raw response:', accounts)
      const mappedAccounts = (accounts || []).map((acc: any) => ({
        accountId: acc.accountId || acc.id || 0,
        accountCode: acc.accountCode || acc.code || '',
        accountName: acc.accountNameEn || acc.accountNameAr || acc.accountName || acc.name || '',
        accountNameEn: acc.accountNameEn || acc.accountName,
        accountNameAr: acc.accountNameAr,
      })).filter((acc: AccountOption) => (acc.accountId || 0) > 0)
      console.log('Mapped accounts:', mappedAccounts.length)
      return mappedAccounts
    } catch (error: any) {
      console.error('Error fetching accounts:', error)
      console.error('Error response:', error.response?.data)
      console.error('Error URL:', error.config?.url)
      return []
    }
  }

  /**
   * Get salary by employee ID and contract ID
   */
  async getSalaryByEmployeeId(empId: number, contractId: number): Promise<any> {
    try {
      const url = endpoints.salary.getByEmployeeId(empId, contractId)
      const response = await apiClient.get(url)
      return response.data
    } catch (error: any) {
      console.error('Failed to get salary:', error)
      throw error
    }
  }

  async getLeaveBalances(
    empId: number,
    branchId: number
  ): Promise<{
    empId: number
    branchId: number
    sick?: { entitlement?: number; availed?: number; balance?: number }
    casual?: { entitlement?: number; availed?: number; balance?: number }
    annual?: { accrualRate?: number; serviceDays?: number; entitlement?: number; availed?: number; balance?: number }
  } | null> {
    try {
      const url = endpoints.contracts.getLeaveBalances(empId, branchId)
      const response = await apiClient.get(url)
      const payload = response.data

      if (payload && typeof payload === 'object') {
        if ('success' in payload && 'data' in payload) return payload.data ?? null
        if ('data' in payload) return payload.data ?? null
      }

      return payload ?? null
    } catch (error: any) {
      console.error('Failed to get leave balances:', error?.response?.data ?? error)
      return null
    }
  }

  /**
   * Build JSON payload for SaveContract/UpdateContract API (camelCase, application/json)
   */
  private buildContractJsonPayload(contract: Contract, branchId: number): Record<string, unknown> {
    const formatDate = (date: string | undefined): string => {
      if (!date) return new Date().toISOString()
      if (date.includes('T')) return date
      return new Date(date).toISOString()
    }
    return {
      id: contract.id ?? 0,
      empId: contract.empId ?? 0,
      projectId: contract.projectId ?? 0,
      gradeId: contract.gradeId ?? 0,
      leavePolicyAccessible: contract.leavePolicyAccessible ?? false,
      contractMonths: contract.contractMonths ?? 0,
      contractStartDate: formatDate(contract.contractStartDate),
      contractEndDate: formatDate(contract.contractEndDate),
      contractDate: formatDate(contract.contractDate),
      contractstatus: contract.contractstatus ?? 0,
      leavesAvailed: contract.leavesAvailed ?? 0,
      leavesBalance: contract.leavesBalance ?? 0,
      casualLeavesAvailed: contract.casualLeavesAvailed ?? 0,
      casualLeavesBalance: contract.casualLeavesBalance ?? 0,
      sickLeavesAvailed: contract.sickLeavesAvailed ?? 0,
      sickLeavesBalance: contract.sickLeavesBalance ?? 0,
      isRenewal: contract.isRenewal ?? 0,
      renewalStartDate: formatDate(contract.renewalStartDate),
      renewalEndDate: formatDate(contract.renewalEndDate),
      annualLeavesEntitlement: contract.annualLeavesEntitlement ?? 0,
      leaveAccuralRate: contract.leaveAccuralRate ?? 0,
      casualLeavesEntitlement: contract.casualLeavesEntitlement ?? 0,
      sickLeavesEntitlement: contract.sickLeavesEntitlement ?? 0,
      probationPeriod: contract.probationPeriod ?? 0,
      workingDays: contract.workingDays ?? 0,
      additionalJob: contract.additionalJob ?? '',
      basicSalary: contract.basicSalary ?? 0,
      housingAllowance: contract.housingAllowance ?? 0,
      transportationAllowance: contract.transpotationAllowance ?? 0,
      mealAllowance: contract.mealAllowance ?? 0,
      gosiDeductions: contract.gosiDeduction ?? contract.gosiDeductions ?? 0,
      additionalJobSalary: contract.additionalJobSalary ?? 0,
      additionalJobExpiry: formatDate(contract.additionalJobExpiry),
      gosiType: Array.isArray(contract.gosiType) ? contract.gosiType : [],
      branchId: branchId ?? 0,
    }
  }

  /**
   * Save new contract (sends JSON - API expects application/json)
   */
  async saveContract(contract: Contract, branchId: number): Promise<any> {
    const url = endpoints.contracts.save
    const payload = this.buildContractJsonPayload(contract, branchId)
    console.log('🔵 [ContractService.saveContract] Payload structure being sent (SaveContract API):', payload)
    console.log('🔵 [ContractService.saveContract] Payload as JSON string:', JSON.stringify(payload, null, 2))
    try {
      const response = await apiClient.post(url, payload, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      })
      console.log('✅ [ContractService.saveContract] Success:', response.data)
      return response.data
    } catch (error: any) {
      console.error('❌ [ContractService.saveContract] Error:', error)
      console.error('Error response:', error.response?.data)
      console.error('Error status:', error.response?.status)
      throw error
    }
  }

  /**
   * Update existing contract (sends JSON - API expects application/json)
   */
  async updateContract(contract: Contract, branchId: number): Promise<any> {
    const url = endpoints.contracts.update
    const payload = this.buildContractJsonPayload(contract, branchId)
    console.log('🔵 [ContractService.updateContract] Payload structure being sent (UpdateContract API):', payload)
    console.log('🔵 [ContractService.updateContract] Payload as JSON string:', JSON.stringify(payload, null, 2))
    try {
      const response = await apiClient.put(url, payload, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      })
      console.log('✅ [ContractService.updateContract] Success:', response.data)
      return response.data
    } catch (error: any) {
      console.error('❌ [ContractService.updateContract] Error:', error)
      console.error('Error response:', error.response?.data)
      console.error('Error status:', error.response?.status)
      throw error
    }
  }

  /**
   * Renew contract (POST /api/Contract/RenewContract with same JSON structure)
   */
  async renewContract(contract: Contract, branchId: number): Promise<any> {
    const url = endpoints.contracts.renew
    const payload = this.buildContractJsonPayload(contract, branchId)
    try {
      const response = await apiClient.post(url, payload, {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
      })
      return response.data
    } catch (error: any) {
      console.error('ContractService.renewContract error:', error?.response?.data ?? error)
      throw error
    }
  }
}

export const contractService = new ContractService()
